<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-select
              v-model="selectedCountry"
              :loading="loadingCountries"
              :placeholder="
                $t('message.please_select') + $t('attributes.country')
              "
              @input="onFilter"
            >
              <option value="">
                {{ $t('attributes.all') }}
              </option>
              <option
                v-for="option in countries"
                :value="option"
                :key="option.code"
              >
                {{ option.code + option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="name"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
          <b-field>
            <b-input
              v-model="englishName"
              :placeholder="
                $t('attributes.search') + $t('attributes.english_name')
              "
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
          <b-field>
            <b-button
              :loading="sending"
              class="is-primary"
              icon-left="plus"
              @click="onCreate"
            >
              {{ $t('attributes.add') + $t('attributes.city_weather') }}
            </b-button>
          </b-field>
        </b-field>
      </div>
    </nav>
    <b-table
      :data="weatherCities"
      :loading="isLoading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      paginated
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="name"
        :label="$t('attributes.city_name')"
        width="150"
        sortable
        numeric
        centered
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>
      <b-table-column
        field="english_name"
        :label="$t('attributes.english_name')"
        width="200"
        sortable
        numeric
        centered
        v-slot="props"
      >
        {{ props.row.english_name }}
      </b-table-column>
      <b-table-column
        field="time_zone"
        :label="$t('attributes.city_gmt')"
        width="150"
        sortable
        numeric
        centered
        v-slot="props"
      >
        {{ props.row.time_zone }}
      </b-table-column>
      <b-table-column
        field="update_date"
        :label="$t('attributes.information') + $t('attributes.modify_date')"
        width="200"
        sortable
        numeric
        centered
        v-slot="props"
      >
        {{ formatDateTime(props.row.update_date) }}
      </b-table-column>
      <b-table-column
        :label="$t('attributes.function')"
        width="250"
        centered
        v-slot="props"
      >
        <b-button type="is-info" @click.prevent.stop="onModify(props.row)">
          {{ $t('attributes.modify') }}
        </b-button>
      </b-table-column>
    </b-table>

    <b-modal :active.sync="isModelActive" has-modal-card>
      <weather-form
        :sending="sending"
        :countries="countries"
        :detail="selectedItem"
        :isCreate="isCreate"
        @onSend="onSend"
      />
    </b-modal>
  </section>
</template>

<script>
import { WEATHER_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'
import WeatherForm from '@/components/basic/WeatherForm'

export default {
  name: 'Weather',
  components: { WeatherForm },
  data() {
    return {
      selectedCountry: null,
      loadingCountries: false,
      countries: [],
      name: '',
      englishName: '',

      weatherCities: [],
      selectedItem: [],
      total: 0,
      perPage: 10,
      page: 1,
      defaultSortOrder: 'asc',
      sortField: 'code',
      sortOrder: 'asc',
      sending: false,
      isLoading: false,
      isCreate: false,
      isModelActive: false
    }
  },
  computed: {},
  mounted() {
    this.loadCountries()
  },
  methods: {
    async loadCountries() {
      const api = `${WEATHER_API}/countries`

      try {
        const res = await this.axios.get(api)
        this.countries = res.data.data
        this.selectedCountry = null
      } catch (e) {
        this.countries = []
        this.selectedCountry = null
        notifyService.fetch_error('country', e)
      }
    },
    async loadWeatherCities() {
      this.isLoading = true
      const api = `${WEATHER_API}/list`
      try {
        const params = {
          countryCode: this.selectedCountry && this.selectedCountry.code,
          name: this.name,
          englishName: this.englishName
        }
        const res = await this.axios.get(api, { params })
        this.weatherCities = res.data.data
        this.total = this.weatherCities.length
        this.isLoading = false
      } catch (e) {
        this.isLoading = false
        this.total = 0
        this.weatherCities = []
        notifyService.fetch_error('country', e)
      }
    },
    onPageChange(page) {
      this.page = page
      this.loadWeatherCities()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadWeatherCities()
    },
    onModify(data) {
      this.selectedItem = data
      this.isCreate = false
      this.isModelActive = true
    },
    onCreate() {
      this.selectedItem = {
        id: 0,
        name: '',
        english_name: '',
        time_zone: '',
        country_code: ''
      }
      this.isCreate = true
      this.isModelActive = true
    },
    onSend(item) {
      if (this.isCreate) {
        this.createWeatherCity(item)
      } else {
        this.updateWeatherCity(item)
      }
    },
    async updateWeatherCity(item) {
      try {
        const api = `${WEATHER_API}/update`
        this.sending = true
        await this.axios.put(api, item)

        notifyService.success(this.$t('attributes.update_succeed'))
        await this.loadWeatherCities()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async createWeatherCity(item) {
      try {
        const api = `${WEATHER_API}/create`
        this.sending = true
        await this.axios.post(api, item)

        this.isCreateModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))
        await this.loadWeatherCities()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    onFilter: debounce(function() {
      this.loadWeatherCities()
    }, 500)
  }
}
</script>
